import {
  defaultTo,
  path,
  pipe,
  map,
  equals,
  ifElse,
  prop,
  propEq,
  curry,
  converge,
  always,
  complement,
  __,
  isNil,
  when,
  divide,
  multiply,
  filter,
  identity,
  identical,
  both,
  cond,
  T,
} from 'ramda';
import sumBy from 'lodash/fp/sumBy';
import moment from 'moment';
import { AUDIENCE_TYPE, CAMPAIGN_FILTER_ALL } from './constants';
import { activeShopPhoto, activeShopPhotoForBarber, activeShopLogo, activeShopLogoForBarber } from '../auth/selectors';
import { mapBySelector, round } from '../common/helpers/functional';
import config from '../../config';

export const getBackgroundImage = state =>
  pipe(
    path(['engagement', 'campaign', 'backgroundImage']),
    defaultTo(activeShopPhoto(state)),
    defaultTo(activeShopPhotoForBarber(state)),
  )(state);

const getType = prop('name');
const isBarber = pipe(getType, equals(AUDIENCE_TYPE.barber));
const isShop = pipe(getType, equals(AUDIENCE_TYPE.shop));
export const getByType = (barberModifier, shopModifier, defaultModifier) =>
  cond([
    [isBarber, barberModifier],
    [isShop, shopModifier],
    [T, defaultModifier],
  ]);

export const getSelectedAudienceKeys = (state) => {
  const getBarberId = prop('barberId');
  const getShopId = prop('shopId');

  return pipe(path(['engagement', 'campaign', 'customerGroups']), map(
    getByType(getBarberId, getShopId, getType),
  ))(state);
};

export const getCampaign = path(['engagement', 'campaign']);

export const getCampaignId = pipe(getCampaign, prop('id'));

export const getCampaignSubject = pipe(getCampaign, prop('subject'));

export const getCampaignSmsCount = pipe(getCampaign, prop('customersNotifiedViaSms'));


export const getCampaignBody = pipe(getCampaign, prop('body'));

export const getCustomersNotifiedViaSms = path(['engagement', 'campaign', 'customersNotifiedViaSms']);

export const getCampaignCharge = path(['engagement', 'campaignCharge']);

export const getCampaignSchedule = path(['engagement', 'campaignCharge', 'scheduledAt']);
export const getCampaignScheduleDate = pipe(
  getCampaignSchedule,
  moment(__).format(config.dateFormat),
);

export const getCampaignScheduleTime = pipe(
  getCampaignSchedule,
  moment(__).format(config.timeFormat),
);

export const getFilter = path(['engagement', 'filter']);
const getCampaignsList = path(['engagement', 'campaignList', 'list']);
const getCampaignById = curry((state, id) => path(['engagement', 'campaignList', 'byId', id])(state));

const notCampaignStatusAll = complement(identical(__, CAMPAIGN_FILTER_ALL));
const hasFilter = pipe(
  getFilter,
  both(identity, notCampaignStatusAll),
);
export const getCampaigns = state => pipe(
  mapBySelector(getCampaignById, getCampaignsList),
  when(
    () => hasFilter(state),
    filter(propEq('status', getFilter(state))),
  ),
)(state);

export const getOpenRate = pipe(
    getCampaigns,
    converge(divide, [sumBy('totalUniqueOpenRateCounter'), sumBy('totalCustomersNotified')]),
    when(
      complement(isFinite),
      always(0)
    ),
    multiply(100),
    round(__, 1),
  );

export const getLogoWithDefault = state =>
  pipe(
    path(['engagement', 'campaign', 'logoImage']),
    defaultTo(activeShopLogo(state)),
    defaultTo(activeShopLogoForBarber(state)),
  )(state);

export const getLogo = ifElse(
  pipe(getCampaignId, isNil),
  getLogoWithDefault,
  path(['engagement', 'campaign', 'logoImage']),
);

export const getNewCustomerWeeks = path(['engagement', 'customerGroups', 'new', 'weeks']);

export const getServiceCustomerGroupServiceId = path(['engagement', 'customerGroups', 'service', 'serviceId']);
